/**
 * Workspace-specific design helpers (on top of components/em/ui).
 * Matches the EM design handoff: progress bars, KPI cards, filter/segmented
 * pills, project colour badge, money formatting.
 */
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, ChevronDown, MoreVertical } from 'lucide-react';
import { T } from '../../components/em/ui';
import { wsApi, assetUrl } from './wsApi';
import { initials, avatarColor, COLOR_SWATCHES } from './wsConstants';

export const money = (n) => '₹' + Math.round(Number(n) || 0).toLocaleString('en-IN');

// Logo/image picker with upload + preview; falls back to a coloured initial.
export const LogoPicker = ({ value, onChange, color = T.orange, label = '', size = 64 }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const pick = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const { url } = await wsApi.uploadImage(file);
      onChange(url);
    } catch { /* surfaced by caller toasts elsewhere */ }
    finally { setBusy(false); }
  };
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
      <div
        onClick={() => ref.current?.click()}
        title="Upload logo"
        style={{
          width: size, height: size, borderRadius: 12, cursor: 'pointer', flexShrink: 0,
          background: value ? '#fff' : color, color: '#fff', overflow: 'hidden',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          border: `1px solid ${T.border}`, font: `700 ${Math.round(size * 0.32)}px ${T.font}`,
        }}
      >
        {value
          ? <img src={assetUrl(value)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          : initials(label)}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <button type="button" onClick={() => ref.current?.click()} disabled={busy}
          style={{ border: `1px solid ${T.border}`, background: 'var(--em-bg-card)', color: T.fg2,
            borderRadius: 8, padding: '6px 12px', cursor: 'pointer', font: `600 12px ${T.font}` }}>
          {busy ? 'Uploading…' : value ? 'Replace' : 'Upload logo'}
        </button>
        {value && (
          <button type="button" onClick={() => onChange('')}
            style={{ border: 0, background: 'none', color: T.danger, cursor: 'pointer', font: `500 11px ${T.font}`, textAlign: 'left', padding: 0 }}>
            Remove
          </button>
        )}
      </div>
      <input ref={ref} type="file" accept="image/*" style={{ display: 'none' }}
        onChange={(e) => { pick(e.target.files?.[0]); e.target.value = ''; }} />
    </div>
  );
};

// Single avatar — photo when available, else coloured initials.
export const Avatar = ({ member = {}, size = 28, style = {} }) => {
  const photo = member.profile_photo
    ? assetUrl(member.profile_photo.startsWith('/') ? member.profile_photo : `/${member.profile_photo}`)
    : null;
  const name = member.full_name || member.username || '?';
  return (
    <div title={name}
      style={{ width: size, height: size, borderRadius: '50%',
        background: photo ? '#fff' : avatarColor(name),
        color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
        font: `700 ${Math.round(size * 0.4)}px ${T.font}`, overflow: 'hidden', flexShrink: 0, ...style }}>
      {photo ? <img src={photo} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : initials(name)}
    </div>
  );
};

// Overlapping avatar stack — shows up to `max`, then a +N chip with a tooltip.
export const AvatarStack = ({ members = [], max = 4, size = 28 }) => {
  const shown = members.slice(0, max);
  const rest = members.slice(max);
  const restNames = rest.map((m) => m.full_name || m.username).filter(Boolean).join(', ');
  return (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      {shown.map((m, i) => (
        <Avatar key={m.user_id || i} member={m} size={size}
          style={{ marginLeft: i ? -8 : 0, border: '2px solid var(--em-bg-card)' }} />
      ))}
      {rest.length > 0 && (
        <div title={restNames}
          style={{ width: size, height: size, borderRadius: '50%', marginLeft: -8,
            border: '2px solid var(--em-bg-card)', background: T.bgMuted, color: T.fg4,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            font: `700 ${Math.round(size * 0.36)}px ${T.font}`, flexShrink: 0 }}>
          +{rest.length}
        </div>
      )}
    </div>
  );
};

// Searchable member combobox — dropdown opens on focus/typing; selected people
// render as avatar chips with an × below the field. `users` = [{id,username,email}].
export const MemberCombobox = ({ users = [], selected = [], onToggle, placeholder = 'Search people…' }) => {
  const [qy, setQy] = useState('');
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const sel = new Set(selected.map(Number));
  const filtered = users.filter((u) =>
    !qy || (u.username || '').toLowerCase().includes(qy.toLowerCase()) ||
    (u.email || '').toLowerCase().includes(qy.toLowerCase()));
  const chips = users.filter((u) => sel.has(Number(u.id)));

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [open]);

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <input value={qy} placeholder={placeholder}
        onChange={(e) => { setQy(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        style={{ width: '100%', boxSizing: 'border-box', border: `1px solid ${T.border}`,
          borderRadius: 8, padding: '8px 11px', font: `400 13px ${T.font}`, outline: 'none',
          background: 'var(--em-bg-card)', color: T.fg1 }} />

      {open && (
        <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, zIndex: 40,
          maxHeight: 220, overflowY: 'auto', background: 'var(--em-bg-card)',
          border: `1px solid ${T.border}`, borderRadius: 10, boxShadow: '0 12px 32px -8px rgba(10,10,10,0.18)' }}>
          {filtered.map((u) => {
            const on = sel.has(Number(u.id));
            return (
              <div key={u.id} onClick={() => onToggle(u.id)}
                style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 11px', cursor: 'pointer',
                  borderBottom: `1px solid ${T.divider}`, background: on ? T.orange50 : 'transparent' }}>
                <Avatar member={u} size={24} />
                <span style={{ flex: 1, minWidth: 0, font: `500 13px ${T.font}`, color: T.fg1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {u.username}
                  {u.email && <span style={{ marginLeft: 8, font: `400 11px ${T.font}`, color: T.fg7 }}>{u.email}</span>}
                </span>
                {on && <span style={{ color: T.orange, font: `700 12px ${T.font}` }}>✓</span>}
              </div>
            );
          })}
          {!filtered.length && <div style={{ padding: 14, textAlign: 'center', color: T.fg7, font: `400 12px ${T.font}` }}>No people found.</div>}
        </div>
      )}

      {chips.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
          {chips.map((u) => (
            <span key={u.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '3px 8px 3px 3px', borderRadius: 9999, border: `1px solid ${T.border}`,
              background: 'var(--em-bg-card)', font: `500 12px ${T.font}`, color: T.fg2 }}>
              <Avatar member={u} size={20} />
              {u.username}
              <button type="button" title="Remove" onClick={() => onToggle(u.id)}
                style={{ background: 'none', border: 0, cursor: 'pointer', color: T.fg7, display: 'flex', padding: 0 }}>
                <X size={12} />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

// Colour field — shows the current colour; opens a swatch-grid panel on click.
export const ColorSwatchSelect = ({ value, onChange, colors = COLOR_SWATCHES }) => {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [open]);

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <button type="button" onClick={() => setOpen((o) => !o)}
        style={{ display: 'flex', alignItems: 'center', gap: 9, width: '100%', boxSizing: 'border-box',
          border: `1px solid ${T.border}`, borderRadius: 8, padding: '8px 11px', cursor: 'pointer',
          background: 'var(--em-bg-card)', font: `500 13px ${T.font}`, color: T.fg1, textAlign: 'left' }}>
        <span style={{ width: 16, height: 16, borderRadius: '50%', background: value || colors[0], flexShrink: 0, border: '1px solid rgba(0,0,0,0.08)' }} />
        <span style={{ flex: 1 }}>{value || colors[0]}</span>
        <ChevronDown size={14} color={T.fg7} style={{ flexShrink: 0 }} />
      </button>
      {open && (
        <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 40,
          display: 'grid', gridTemplateColumns: 'repeat(4, 28px)', gap: 8, padding: 12,
          background: 'var(--em-bg-card)', border: `1px solid ${T.border}`, borderRadius: 10,
          boxShadow: '0 12px 32px -8px rgba(10,10,10,0.18)' }}>
          {colors.map((c) => (
            <button key={c} type="button" title={c}
              onClick={() => { onChange(c); setOpen(false); }}
              style={{ width: 28, height: 28, borderRadius: 8, background: c, cursor: 'pointer',
                border: value === c ? '2px solid var(--em-fg1)' : '2px solid transparent', padding: 0 }} />
          ))}
        </div>
      )}
    </div>
  );
};

export const ProgressBar = ({ pct, color = T.orange, height = 6 }) => (
  <div style={{ flex: 1, height, borderRadius: 9999, background: T.bgMuted, overflow: 'hidden', minWidth: 40 }}>
    <div style={{ height: '100%', width: `${Math.max(0, Math.min(100, pct))}%`, background: color, borderRadius: 9999, transition: 'width 300ms' }} />
  </div>
);

export const KpiCard = ({ Icon, label, value, sub, color, bg }) => (
  <div style={{ background: 'var(--em-bg-card)', border: `1px solid ${T.border}`, borderRadius: 14, padding: '18px 20px', boxShadow: T.shadowSm }}>
    <div style={{ width: 38, height: 38, borderRadius: 10, background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 14 }}>
      <Icon size={18} color={color} />
    </div>
    <div style={{ font: `800 26px/1 ${T.font}`, letterSpacing: '-0.03em', color: T.fg1, fontVariantNumeric: 'tabular-nums' }}>{value}</div>
    <div style={{ font: `500 12px ${T.font}`, color: T.fg4, marginTop: 6 }}>{label}</div>
    {sub != null && <div style={{ font: `400 11px ${T.font}`, color: T.fg7, marginTop: 2 }}>{sub}</div>}
  </div>
);

// Pill group — rounded segmented control (filters / view toggles).
export const Pills = ({ options, value, onChange, style = {} }) => (
  <div style={{ display: 'inline-flex', gap: 2, padding: 4, borderRadius: 9999, background: T.bgMuted, ...style }}>
    {options.map((o) => {
      const on = value === o.value;
      const Icon = o.icon;
      return (
        <button key={o.value} onClick={() => onChange(o.value)} style={{
          display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 14px', borderRadius: 9999,
          border: 0, cursor: 'pointer', background: on ? 'var(--em-bg-card)' : 'transparent',
          color: on ? T.orange : T.fg5, font: `${on ? 600 : 500} 12px ${T.font}`,
          boxShadow: on ? '0 1px 2px rgba(0,0,0,0.06)' : 'none', transition: 'all .15s',
        }}>
          {Icon && <Icon size={14} />}{o.label}
        </button>
      );
    })}
  </div>
);

export const ColorBadge = ({ color = T.orange, size = 40, label }) => (
  <div style={{ width: size, height: size, borderRadius: 9, background: color, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', font: `700 ${Math.round(size * 0.4)}px ${T.font}`, flexShrink: 0 }}>
    {label || ''}
  </div>
);

// Hover lift for cards — spread onto a card div alongside its style.
export const hoverLift = {
  onMouseEnter: (e) => {
    e.currentTarget.style.boxShadow = '0 8px 24px -10px rgba(10,10,10,0.18)';
    e.currentTarget.style.transform = 'translateY(-2px)';
  },
  onMouseLeave: (e) => {
    e.currentTarget.style.boxShadow = T.shadowSm;
    e.currentTarget.style.transform = 'none';
  },
};
export const hoverLiftStyle = { transition: 'box-shadow 150ms, transform 150ms' };

// Shimmering skeleton block (one-time keyframes injection).
let skeletonCssInjected = false;
const ensureSkeletonCss = () => {
  if (skeletonCssInjected || typeof document === 'undefined') return;
  skeletonCssInjected = true;
  const el = document.createElement('style');
  el.textContent = `@keyframes ws-shimmer { 0% { background-position: -400px 0; } 100% { background-position: 400px 0; } }`;
  document.head.appendChild(el);
};

export const Skeleton = ({ w = '100%', h = 14, radius = 6, style = {} }) => {
  ensureSkeletonCss();
  return (
    <div style={{ width: w, height: h, borderRadius: radius,
      background: 'linear-gradient(90deg, var(--em-bg-muted, #f3f4f6) 25%, var(--em-bg-head, #ececf0) 37%, var(--em-bg-muted, #f3f4f6) 63%)',
      backgroundSize: '400px 100%', animation: 'ws-shimmer 1.3s ease-in-out infinite', ...style }} />
  );
};

// Card-shaped skeleton for list grids.
export const SkeletonCard = ({ height = 150 }) => (
  <div style={{ background: 'var(--em-bg-card)', border: `1px solid ${T.border}`, borderRadius: 14, padding: 18, boxShadow: T.shadowSm, height, boxSizing: 'border-box' }}>
    <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
      <Skeleton w={40} h={40} radius={10} />
      <div style={{ flex: 1 }}>
        <Skeleton w="60%" h={13} style={{ marginBottom: 8 }} />
        <Skeleton w="40%" h={10} />
      </div>
    </div>
    <Skeleton w="100%" h={8} style={{ marginBottom: 12 }} />
    <Skeleton w="50%" h={10} />
  </div>
);

// Grid of skeleton cards sized like the list grids.
export const SkeletonGrid = ({ count = 6, minWidth = 300, height = 150 }) => (
  <div style={{ display: 'grid', gridTemplateColumns: `repeat(auto-fill, minmax(${minWidth}px, 1fr))`, gap: 16 }}>
    {Array.from({ length: count }, (_, i) => <SkeletonCard key={i} height={height} />)}
  </div>
);

/**
 * RowMenu — kebab (⋮) that collapses a row's actions into a dropdown.
 *
 * `items`: [{ key, label, icon, onClick, danger, disabled, hidden }]. Entries
 * with `hidden: true` are dropped, so callers can keep permission checks inline
 * (`hidden: !canApprove`) instead of building the array conditionally.
 *
 * The panel renders through a portal at fixed coordinates rather than as an
 * absolutely-positioned child. List containers here use `overflow: hidden` to
 * clip their rounded corners, which would also clip an in-flow dropdown — and
 * the menu on the last row needs to escape the card entirely. It flips above
 * the button when there isn't room below, and closes on outside click, Escape,
 * scroll, or resize (a fixed panel would otherwise detach from its anchor).
 */
export const RowMenu = ({ items = [], label = 'Actions', size = 16 }) => {
  const btnRef = useRef(null);
  const [pos, setPos] = useState(null);        // null = closed
  const visible = items.filter((i) => !i.hidden);

  const place = () => {
    const r = btnRef.current?.getBoundingClientRect();
    if (!r) return;
    const height = visible.length * 36 + 10;   // ~36px per item + 5px padding each end
    const below = window.innerHeight - r.bottom > height + 8;
    setPos({
      top: below ? r.bottom + 4 : r.top - height - 4,
      right: Math.max(8, window.innerWidth - r.right),
    });
  };

  useEffect(() => {
    if (!pos) return undefined;
    const close = () => setPos(null);
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    document.addEventListener('click', close);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', close, true);   // capture: catches inner scrollers
    window.addEventListener('resize', close);
    return () => {
      document.removeEventListener('click', close);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [pos]);

  if (!visible.length) return null;

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={!!pos}
        title={label}
        onClick={(e) => { e.stopPropagation(); if (pos) setPos(null); else place(); }}
        style={{
          // Fixed 28×28 square: the icon centres inside it, so the button's box
          // is the same width in every row and its right edge lines up with the
          // rest of the column regardless of how the glyph is drawn.
          width: 28, height: 28, padding: 0, boxSizing: 'border-box',
          background: 'none', border: 0, borderRadius: 7,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: 'pointer', color: T.fg7, lineHeight: 0,
          // Kill the black UA focus ring. Chrome paints it after a plain click
          // here, which reads as a stray border around the row's ⋮. The
          // keyboard-only ring below keeps this reachable without a mouse.
          outline: 'none',
        }}
        onMouseEnter={(e) => { e.currentTarget.style.background = T.bgMuted; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; }}
        onFocus={(e) => {
          // Inline styles can't express :focus-visible, so match against it
          // directly and ring up only for keyboard focus. try/catch because
          // browsers without the selector throw a SyntaxError on matches().
          try {
            if (e.currentTarget.matches(':focus-visible')) {
              e.currentTarget.style.boxShadow = `0 0 0 2px ${T.orange}55`;
            }
          } catch { /* no :focus-visible support — skip the ring */ }
        }}
        onBlur={(e) => { e.currentTarget.style.boxShadow = 'none'; }}
      >
        <MoreVertical size={size} />
      </button>
      {pos && createPortal(
        <div
          role="menu"
          onClick={(e) => e.stopPropagation()}
          style={{
            position: 'fixed', top: pos.top, right: pos.right, zIndex: 1000, minWidth: 160,
            background: 'var(--em-bg-card)', border: `1px solid ${T.border}`, borderRadius: 10,
            boxShadow: '0 12px 32px -8px rgba(10,10,10,0.22)', padding: 5,
          }}
        >
          {visible.map((it) => (
            <button
              key={it.key || it.label}
              role="menuitem"
              type="button"
              disabled={it.disabled}
              onClick={() => { setPos(null); it.onClick?.(); }}
              style={{
                display: 'flex', alignItems: 'center', gap: 9, width: '100%', textAlign: 'left',
                padding: '8px 10px', borderRadius: 7, border: 0, background: 'transparent',
                cursor: it.disabled ? 'not-allowed' : 'pointer', opacity: it.disabled ? 0.45 : 1,
                color: it.danger ? T.danger : T.fg2, font: `500 12.5px ${T.font}`,
                outline: 'none',   // hover/keyboard highlight below stands in for the UA ring
              }}
              onFocus={(e) => { if (!it.disabled) e.currentTarget.style.background = T.bgMuted; }}
              onBlur={(e) => { e.currentTarget.style.background = 'transparent'; }}
              onMouseEnter={(e) => { if (!it.disabled) e.currentTarget.style.background = T.bgMuted; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
            >
              {it.icon && <it.icon size={14} />}
              {it.label}
            </button>
          ))}
        </div>,
        document.body,
      )}
    </>
  );
};

export const Panel = ({ title, action, padding = '18px 20px', children, style = {} }) => (
  <div style={{ background: 'var(--em-bg-card)', border: `1px solid ${T.border}`, borderRadius: 14, padding, boxShadow: T.shadowSm, ...style }}>
    {(title || action) && (
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 14 }}>
        <span style={{ font: `700 14px ${T.font}`, color: T.fg1, flex: 1 }}>{title}</span>
        {action}
      </div>
    )}
    {children}
  </div>
);
